import { useState, useCallback, useEffect } from "react";

import {
  Box,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Button,
} from "@material-ui/core";

import {
  Create,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from "@material-ui/icons";

import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import { columnNumberField, columnTextField } from "utils/tables-utils";
import { IsMobile } from "utils/is-mobile";

const getTableColumns = (
  setSelectedReason,
  toggleFormDialog,
  searching,
  setCloseFullscreen
) => [
  {
    dataField: "editar",
    text: "Opções",
    isDummyField: true,
    headerStyle: { width: 45 },
    formatter: (cell, row) => {
      return !searching ? (
        <Tooltip
          title={
            row.DataHoraResolucaoFormat
              ? "Não é possível editar falha com resolução"
              : "Editar Falha"
          }
        >
          <span>
            <IconButton
              aria-label="edit"
              style={{ margin: "-0.5rem 0" }}
              onClick={() => {
                setSelectedReason(row);
                toggleFormDialog("edit");
                setCloseFullscreen(true);
              }}
              disabled={Boolean(row.UsuarioResolucao)}
            >
              <Box
                component={Create}
                width="1.25rem!important"
                height="1.25rem!important"
              />
            </IconButton>
          </span>
        </Tooltip>
      ) : null;
    },
  },
  columnNumberField(
    {
      dataField: "UsuarioLancamento",
      text: "Usuário de registro",
      order: "desc",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnNumberField(
    {
      dataField: "IdTear",
      text: "Número do Tear",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnNumberField(
    {
      dataField: "Tear.Celula",
      text: "Célula",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnNumberField(
    {
      dataField: "IdPista",
      text: "Pista",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnTextField(
    {
      dataField: "Motivo.DescMotivoFalha",
      text: "Motivo",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnTextField(
    {
      dataField: "Observacao",
      text: "Observação",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnNumberField(
    {
      dataField: "QuantidadeFalha",
      text: "Quantidade falhas",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnTextField(
    {
      dataField: "TipoTrativa.Descricao",
      text: "Tratativa",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnTextField(
    {
      dataField: "DataHoraLancamentoFormat",
      text: "Data/Hora de Registro",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
      align: "center",
    },
    true
  ),
  columnTextField(
    {
      dataField: "DataHoraResolucaoFormat",
      text: "Data/Hora de Resolução",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
      align: "center",
    },
    true
  ),
  columnNumberField(
    {
      dataField: "UsuarioResolucao",
      text: "Turno da Resolução",
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
];

const useStyles = makeStyles(componentStyles);

export default function TableMonitorTeares({
  dataTable,
  setSelectedReason,
  toggleFormDialog,
  searching,
  totalRegistros,
  currentPage,
  currentSizePerPage,
  searchFailures,
  fullScreen,
  setFullScreen,
}) {
  const classes = useStyles();
  const elem = document.getElementById("#fullscreenTableMonitorTeares");
  const [loading, setLoading] = useState(false);
  const [closeFullscreen, setCloseFullscreen] = useState(false);

  const isMobile = IsMobile();

  const doSearch = useCallback(
    (page, sizePerPage) => {
      setLoading(true);

      searchFailures({}, page, sizePerPage).then(() => setLoading(false));
    },
    [searchFailures]
  );

  const handlePageChange = useCallback(
    (page, sizePerPage) => {
      doSearch(page, sizePerPage);
    },
    [doSearch]
  );

  const handleSizePerPageChange = useCallback(
    (sizePerPage, page) => {
      doSearch(page, sizePerPage);
    },
    [doSearch]
  );

  const rowStyle = (row) => {
    const style = { backgroundColor: "#fbc6ca" };
    if (!row.UsuarioResolucao) {
      return style;
    }
  };

  function exitHandler() {
    if (
      !document.fullscreenElement &&
      !document.webkitIsFullScreen &&
      !document.mozFullScreen &&
      !document.msFullscreenElement
    ) {
      setFullScreen(false);
    }
  }

  function handleChangeFullscreen() {
    setCloseFullscreen(false);

    if (document.exitFullscreen) {
      document.exitFullscreen();
      document.addEventListener("fullscreenchange", exitHandler);
    } else if (document.mozCancelFullScreen) {
      /* Firefox */
      document.mozCancelFullScreen();
      document.addEventListener("mozfullscreenchange", exitHandler);
    } else if (document.webkitExitFullscreen) {
      /* Chrome, Safari & Opera */
      document.webkitExitFullscreen();
      document.addEventListener("webkitfullscreenchange", exitHandler);
    } else if (document.msExitFullscreen) {
      /* IE/Edge */
      document.msExitFullscreen();
      document.addEventListener("MSFullscreenChange", exitHandler);
    }

    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      /* Firefox */
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Chrome, Safari & Opera */
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      /* IE/Edge */
      elem.msRequestFullscreen();
    }
  }

  useEffect(() => {
    if (closeFullscreen && fullScreen) {
      handleChangeFullscreen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeFullscreen]);

  return (
    <ToolkitTable
      searched={Boolean(dataTable)}
      keyField="IdLancamentoFalha"
      dataTable={dataTable}
      styledRow={rowStyle}
      columnModel={getTableColumns(
        setSelectedReason,
        toggleFormDialog,
        searching,
        setCloseFullscreen
      )}
      headerTitle={`Falhas nos teares`}
      showSearch={false}
      pagination={{
        options: [25, 50, 100],
        defaultOption: currentSizePerPage,
        totalSize: totalRegistros,
        showTotal: true,
        page: currentPage,
        onPageChange: handlePageChange,
        sizePerPage: currentSizePerPage,
        onSizePerPageChange: handleSizePerPageChange,
      }}
      CustomHeader={() => (
        <Grid
          container
          className="pl-4"
          style={{ height: isMobile ? 100 : 60, padding: "0 24px" }}
        >
          <Grid item xs={isMobile ? 12 : 6}>
            <Box component={Typography} variant="h2" marginBottom="0!important">
              <Box component="span">Falhas nos teares</Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={isMobile ? 12 : 6}
            style={
              isMobile
                ? { display: "flex", alignItems: "flex-start" }
                : {
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                  }
            }
          >
            <Button
              variant="contained"
              classes={{
                root: classes.buttonRootWhite,
              }}
              style={{ boxShadow: "none", marginTop: "-0.5rem" }}
              onClick={() => {
                handleChangeFullscreen();
                setFullScreen(!fullScreen);
              }}
              disabled={searching}
            >
              {fullScreen ? (
                <FullscreenExitIcon
                  style={{
                    width: "2.75rem",
                    height: "2.75rem",
                  }}
                />
              ) : (
                <FullscreenIcon
                  style={{
                    width: "2.75rem",
                    height: "2.75rem",
                  }}
                />
              )}
            </Button>
          </Grid>
        </Grid>
      )}
      bootstrapTableProps={{
        remote: true,
        onTableChange: () => {},
      }}
      loading={loading}
      notFoundTitle="Sua pesquisa não encontrou nenhuma falha."
    />
  );
}
